import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { PageHeader, PageBody, StatusBadge, EmptyState, formatMoney } from "../components/Shared";
import { Link } from "react-router-dom";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ChevronRight, ChevronDown, Download } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const ORDER_STATUSES = ["all", "open", "in_progress", "completed", "cancelled"];

export default function SaleReport() {
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({ revenue: {}, cost: {}, profit: {}, alt_cost: {}, alt_profit: {} });
  const [customers, setCustomers] = useState([]);
  const [filters, setFilters] = useState({ customer_id: "all", order_status: "all", date_from: "", date_to: "" });
  const [expanded, setExpanded] = useState({});

  const load = () => {
    const params = {};
    Object.entries(filters).forEach(([k, v]) => { if (v && v !== "all") params[k] = v; });
    return api.get("/reports/sales", { params }).then((r) => { setRows(r.data.rows); setTotals(r.data.totals); });
  };

  useEffect(() => { api.get("/customers").then((r) => setCustomers(r.data)); }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const setF = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const toggle = (id) => setExpanded((e) => ({ ...e, [id]: !e[id] }));

  const downloadXlsx = async () => {
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v && v !== "all") params[k] = v; });
      const res = await api.get("/reports/sales.xlsx", { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
      a.href = url;
      a.download = `icmarkt-sale-report-${stamp}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Sale report exported");
    } catch { toast.error("Failed to export"); }
  };

  const summary = useMemo(() => {
    // if all rows share single primary currency, we can show a headline number
    const cur = Object.keys(totals.revenue);
    return { currencies: cur };
  }, [totals]);

  return (
    <>
      <PageHeader title="Sale Report" subtitle="Profit & margin per order" testid="report-title" />
      <PageBody>
        {/* Filters */}
        <div className="grid md:grid-cols-4 gap-3 mb-4 items-end">
          <div>
            <Label className="label-caps">From date</Label>
            <Input type="date" className="rounded-none mt-1" value={filters.date_from} onChange={(e) => setF("date_from", e.target.value)} data-testid="filter-date-from" />
          </div>
          <div>
            <Label className="label-caps">To date</Label>
            <Input type="date" className="rounded-none mt-1" value={filters.date_to} onChange={(e) => setF("date_to", e.target.value)} data-testid="filter-date-to" />
          </div>
          <div>
            <Label className="label-caps">Customer</Label>
            <Select value={filters.customer_id} onValueChange={(v) => setF("customer_id", v)}>
              <SelectTrigger className="rounded-none mt-1" data-testid="filter-customer"><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-none">
                <SelectItem value="all">All</SelectItem>
                {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="label-caps">Status</Label>
            <Select value={filters.order_status} onValueChange={(v) => setF("order_status", v)}>
              <SelectTrigger className="rounded-none mt-1" data-testid="filter-status"><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-none">
                {ORDER_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* KPIs */}
        {summary.currencies.length > 0 && (
          <div className="grid md:grid-cols-3 gap-4 mb-6">
            {summary.currencies.map((cur) => {
              const r = totals.revenue[cur] || 0;
              const c = totals.cost[cur] || 0;
              const p = totals.profit[cur] || 0;
              const m = r ? (p / r * 100) : 0;
              const tc = (totals.alt_cost || {})[cur] || 0;
              const tp = (totals.alt_profit || {})[cur];
              const tm = r && tc ? ((r - tc) / r * 100) : null;
              return (
                <div key={cur} className="bg-white border border-slate-300 p-5" data-testid={`kpi-${cur}`}>
                  <div className="label-caps">Totals · {cur}</div>
                  <div className="mt-3 flex items-baseline justify-between">
                    <div className="text-xs text-slate-500">Revenue</div>
                    <div className="font-display text-lg text-slate-900">{formatMoney(r, cur)}</div>
                  </div>
                  <div className="mt-1 flex items-baseline justify-between">
                    <div className="text-xs text-slate-500">Cost (primary)</div>
                    <div className="font-mono text-sm text-slate-700">{formatMoney(c, cur)}</div>
                  </div>
                  <div className="mt-1 flex items-baseline justify-between border-t border-slate-200 pt-2">
                    <div className="text-xs font-mono uppercase tracking-wider text-emerald-700">Profit</div>
                    <div className="font-display text-2xl text-emerald-700">{formatMoney(p, cur)}</div>
                  </div>
                  <div className="mt-1 flex items-baseline justify-between">
                    <div className="text-xs text-slate-500">Margin</div>
                    <div className={`font-mono text-sm ${m >= 0 ? "text-emerald-700" : "text-red-700"}`}>{m.toFixed(1)}%</div>
                  </div>
                  {tc > 0 && (
                    <div className="mt-3 pt-2 border-t border-dashed border-slate-200" data-testid={`kpi-alt-${cur}`} title="Scenario: what we'd earn if we bypassed the primary supplier and bought direct from the sub-supplier">
                      <div className="flex items-baseline justify-between">
                        <div className="text-[10px] font-mono uppercase tracking-wider text-blue-700">Alt cost (direct sub)</div>
                        <div className="font-mono text-sm text-slate-700">{formatMoney(tc, cur)}</div>
                      </div>
                      <div className="mt-1 flex items-baseline justify-between">
                        <div className="text-xs text-slate-500">Alt profit</div>
                        <div className={`font-mono text-sm ${(tp || 0) >= 0 ? "text-blue-700" : "text-red-700"}`}>{tp != null ? formatMoney(tp, cur) : "—"}</div>
                      </div>
                      <div className="mt-1 flex items-baseline justify-between">
                        <div className="text-xs text-slate-500">Alt margin</div>
                        <div className={`font-mono text-sm ${(tm || 0) >= 0 ? "text-blue-700" : "text-red-700"}`}>{tm != null ? `${tm.toFixed(1)}%` : "—"}</div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {rows.length === 0 ? <EmptyState title="No orders in range" hint="Adjust the filters or enter purchase prices to see profit." /> :
          <div className="bg-white border border-slate-300 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-300">
                <tr className="text-left">
                  <th className="py-2 px-3 w-6"></th>
                  <th className="py-2 px-3 label-caps">Order #</th>
                  <th className="py-2 px-3 label-caps">Customer</th>
                  <th className="py-2 px-3 label-caps">Date</th>
                  <th className="py-2 px-3 label-caps text-right">Items</th>
                  <th className="py-2 px-3 label-caps text-right">Revenue</th>
                  <th className="py-2 px-3 label-caps text-right">Cost</th>
                  <th className="py-2 px-3 label-caps text-right">Profit</th>
                  <th className="py-2 px-3 label-caps text-right">Margin %</th>
                  <th className="py-2 px-3 label-caps text-right text-blue-700" title="Margin if we bypassed the primary supplier and bought direct from sub">Alt Margin %</th>
                  <th className="py-2 px-3 label-caps">Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const cur = r.primary_currency;
                  const isOpen = !!expanded[r.order_id];
                  const margin = r.margin_pct;
                  return (
                    <React.Fragment key={r.order_id}>
                      <tr className="border-b border-slate-200 hover:bg-slate-50" data-testid={`report-row-${r.order_id}`}>
                        <td className="py-2 px-3">
                          <button onClick={() => toggle(r.order_id)} className="text-slate-500 hover:text-slate-900" data-testid={`toggle-${r.order_id}`}>
                            {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                          </button>
                        </td>
                        <td className="py-2 px-3 font-mono"><Link to={`/orders/${r.order_id}`} className="text-blue-700 hover:text-blue-900">{r.order_number}</Link></td>
                        <td className="py-2 px-3">{r.customer_name}</td>
                        <td className="py-2 px-3 font-mono text-slate-500">{r.created_at?.slice(0, 10)}</td>
                        <td className="py-2 px-3 font-mono text-right">{r.item_count}</td>
                        <td className="py-2 px-3 font-mono text-right">{cur ? formatMoney(r.revenue, cur) : "-"}</td>
                        <td className="py-2 px-3 font-mono text-right">{cur ? formatMoney(r.cost, cur) : "-"}</td>
                        <td className={`py-2 px-3 font-mono text-right ${r.profit >= 0 ? "text-emerald-700" : "text-red-700"}`}>{cur ? formatMoney(r.profit, cur) : "-"}</td>
                        <td className={`py-2 px-3 font-mono text-right ${margin == null ? "text-slate-400" : margin >= 0 ? "text-emerald-700" : "text-red-700"}`}>{margin != null ? `${margin.toFixed(1)}%` : "—"}</td>
                        <td className={`py-2 px-3 font-mono text-right ${r.alt_margin_pct == null ? "text-slate-400" : r.alt_margin_pct >= 0 ? "text-blue-700" : "text-red-700"}`} data-testid={`alt-margin-${r.order_id}`}>{r.alt_margin_pct != null ? `${r.alt_margin_pct.toFixed(1)}%` : "—"}</td>
                        <td className="py-2 px-3"><StatusBadge status={r.status} /></td>
                      </tr>
                      {isOpen && (
                        <tr className="border-b border-slate-200 bg-slate-50/60">
                          <td></td>
                          <td colSpan={10} className="py-3 px-3">
                            <div className="border border-slate-300 bg-white">
                              <table className="w-full text-xs">
                                <thead className="bg-slate-50 border-b border-slate-300">
                                  <tr className="text-left">
                                    <th className="py-1.5 px-2 label-caps">Part #</th>
                                    <th className="py-1.5 px-2 label-caps">Mfr</th>
                                    <th className="py-1.5 px-2 label-caps text-right">Qty</th>
                                    <th className="py-1.5 px-2 label-caps text-right">Unit</th>
                                    <th className="py-1.5 px-2 label-caps text-right">Revenue</th>
                                    <th className="py-1.5 px-2 label-caps text-right">Primary Cost</th>
                                    <th className="py-1.5 px-2 label-caps text-right">Sub Cost</th>
                                    <th className="py-1.5 px-2 label-caps text-right" title="Line profit = revenue − primary cost (our real cost)">Line Profit</th>
                                    <th className="py-1.5 px-2 label-caps text-right text-blue-700" title="Scenario: profit if we bypassed the primary supplier and bought direct from sub">Alt Line Profit</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {r.lines.map((l) => {
                                    const lineProfit = l.cost != null ? (l.revenue - l.cost) : null;
                                    const altLineProfit = l.alt_cost != null ? (l.revenue - l.alt_cost) : null;
                                    return (
                                      <tr key={l.item_id} className="border-b border-slate-100">
                                        <td className="py-1.5 px-2 font-mono">{l.part_number}</td>
                                        <td className="py-1.5 px-2">{l.manufacturer || "-"}</td>
                                        <td className="py-1.5 px-2 font-mono text-right">{l.quantity}</td>
                                        <td className="py-1.5 px-2 font-mono text-right">{formatMoney(l.unit_price, l.currency)}</td>
                                        <td className="py-1.5 px-2 font-mono text-right">{formatMoney(l.revenue, l.currency)}</td>
                                        <td className="py-1.5 px-2 font-mono text-right">{l.primary_cost != null ? formatMoney(l.primary_cost, l.primary_currency || l.currency) : "—"}</td>
                                        <td className="py-1.5 px-2 font-mono text-right">{l.sub_cost != null ? formatMoney(l.sub_cost, l.sub_currency || l.currency) : "—"}</td>
                                        <td className={`py-1.5 px-2 font-mono text-right ${lineProfit == null ? "text-slate-400" : lineProfit >= 0 ? "text-emerald-700" : "text-red-700"}`}>{lineProfit != null ? formatMoney(lineProfit, l.currency) : "—"}</td>
                                        <td className={`py-1.5 px-2 font-mono text-right ${altLineProfit == null ? "text-slate-400" : altLineProfit >= 0 ? "text-blue-700" : "text-red-700"}`}>{altLineProfit != null ? formatMoney(altLineProfit, l.currency) : "—"}</td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        }
      </PageBody>
    </>
  );
}
